import { titleCase } from 'title-case';
import lowerCase from 'lodash/lowerCase';

/**
 * Returns true if the two values are equal by value (deep for objects/arrays).
 */
function elementEqual(orig: unknown, changed: unknown): boolean {
  if (orig === changed) return true;
  if (orig === null || changed === null) return orig === changed;
  if (orig instanceof Date && changed instanceof Date) {
    return orig.getTime() === changed.getTime();
  }
  if (orig instanceof Date || changed instanceof Date) {
    return false;
  }
  if (typeof orig !== 'object' || typeof changed !== 'object') return orig === changed;
  if (Array.isArray(orig) && Array.isArray(changed)) {
    if (orig.length !== changed.length) return false;
    return orig.every((_, i) => elementEqual(orig[i], changed[i]));
  }
  if (Array.isArray(orig) || Array.isArray(changed)) return false;
  const [b, a] = differences(orig as object, changed as object);
  return Object.keys(b).length === 0 && Object.keys(a).length === 0;
}

/**
 * Compares two objects and returns the differences between them.
 * Only differing keys and nested paths are included; identical nested objects or array elements are omitted.
 * @param original The original object
 * @param changed The changed object
 * @returns An array with the before and after objects
 */
export function differences(original: object, changed: object): [object, object] {
  const keys = Object.keys(original);
  const before: Record<string, unknown> = {};
  const after: Record<string, unknown> = {};
  for (const key of keys) {
    if (Array.isArray(original[key]) && Array.isArray(changed[key])) {
      const origArr = original[key] as unknown[];
      const changedArr = changed[key] as unknown[];
      if (origArr.length !== changedArr.length) {
        before[key] = origArr;
        after[key] = changedArr;
      } else if (arrayElementsHaveIds(origArr) && arrayElementsHaveIds(changedArr)) {
        const origById = new Map(origArr.map((el) => [getIdentifiableId(el) as string, el]));
        const beforeArr: unknown[] = [];
        const afterArr: unknown[] = [];
        let hasDiff = false;
        for (const changedEl of changedArr) {
          const id = getIdentifiableId(changedEl) as string;
          const origEl = origById.get(id);
          if (origEl === undefined) {
            beforeArr.push({});
            afterArr.push(changedEl);
            hasDiff = true;
            continue;
          }
          if (elementEqual(origEl, changedEl)) {
            beforeArr.push({ id });
            afterArr.push({ id });
          } else if (isRecord(origEl) && isRecord(changedEl)) {
            const [b, a] = differences(origEl, changedEl);
            beforeArr.push({ id, ...(b as Record<string, unknown>) });
            afterArr.push({ id, ...(a as Record<string, unknown>) });
            hasDiff = true;
          } else {
            beforeArr.push(origEl);
            afterArr.push(changedEl);
            hasDiff = true;
          }
        }
        if (arrayIdMembershipChanged(origArr, changedArr)) {
          before[key] = origArr;
          after[key] = changedArr;
        } else if (hasDiff) {
          before[key] = beforeArr;
          after[key] = afterArr;
        }
      } else if (arrayElementsHaveIds(origArr) || arrayElementsHaveIds(changedArr)) {
        // Mixed id coverage (e.g. remove one row and append a new row without id at same length):
        // index-based patches would corrupt membership — replace the whole array.
        if (!elementEqual(origArr, changedArr)) {
          before[key] = origArr;
          after[key] = changedArr;
        }
      } else if (arrayElementsArePrimitives(origArr) || arrayElementsArePrimitives(changedArr)) {
        // Scalar lists (String[], Int[], …): replace wholesale. Index patches with `{}`
        // placeholders are invalid for ZenStack/Prisma scalar array updates.
        if (!elementEqual(origArr, changedArr)) {
          before[key] = origArr;
          after[key] = changedArr;
        }
      } else {
        const beforeArr: unknown[] = [];
        const afterArr: unknown[] = [];
        let hasDiff = false;
        for (let i = 0; i < origArr.length; i++) {
          const o = origArr[i];
          const c = changedArr[i];
          if (elementEqual(o, c)) {
            beforeArr.push({});
            afterArr.push({});
          } else {
            if (
              typeof o === 'object' &&
              o !== null &&
              typeof c === 'object' &&
              c !== null &&
              !Array.isArray(o) &&
              !Array.isArray(c)
            ) {
              const [b, a] = differences(o as object, c as object);
              beforeArr.push(b);
              afterArr.push(a);
            } else {
              beforeArr.push(o);
              afterArr.push(c);
            }
            hasDiff = true;
          }
        }
        if (hasDiff) {
          before[key] = beforeArr;
          after[key] = afterArr;
        }
      }
    } else if (original[key] === null || changed[key] === null) {
      if (original[key] !== null || changed[key] !== null) {
        if (original[key] !== undefined) {
          before[key] = original[key];
        }
        if (changed[key] !== undefined) {
          after[key] = changed[key];
        }
      }
    } else if (original[key] instanceof Date && changed[key] instanceof Date) {
      if (original[key].getTime() !== changed[key].getTime()) {
        before[key] = original[key];
        after[key] = changed[key];
      }
    } else if (typeof original[key] === 'object' && typeof changed[key] === 'object') {
      const [b, a] = differences(original[key] || {}, changed[key] || {});
      if (Object.keys(b).length > 0) before[key] = b;
      if (Object.keys(a).length > 0) after[key] = a;
    } else if (typeof original[key] === 'object' || typeof changed[key] === 'object') {
      if (original[key] !== undefined) {
        before[key] = original[key];
      }
      if (changed[key] !== undefined) {
        after[key] = changed[key];
      }
    } else if (original[key] !== changed[key]) {
      before[key] = original[key];
      after[key] = changed[key];
    }
  }
  return [before, after];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * True when every element is a JSON scalar (or Date). Empty arrays count as primitive.
 * Used so String[] / Int[] diffs replace the whole list instead of emitting `{}` slots.
 */
function isPrimitiveArrayElement(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  const valueType = typeof value;
  return (
    valueType === 'string' ||
    valueType === 'number' ||
    valueType === 'boolean' ||
    valueType === 'bigint' ||
    value instanceof Date
  );
}

function arrayElementsArePrimitives(arr: unknown[]): boolean {
  return arr.every(isPrimitiveArrayElement);
}

function getIdentifiableId(value: unknown): string | undefined {
  if (!isRecord(value)) return undefined;
  const id = value.id;
  return typeof id === 'string' ? id : undefined;
}

function arrayElementsHaveIds(arr: unknown[]): boolean {
  return arr.length > 0 && arr.every((el) => getIdentifiableId(el) !== undefined);
}

/** True when two same-length id-bearing arrays do not contain the same set of ids. */
function arrayIdMembershipChanged(original: unknown[], changed: unknown[]): boolean {
  const originalIds = new Set(original.map((el) => getIdentifiableId(el) as string));
  const changedIds = new Set(changed.map((el) => getIdentifiableId(el) as string));
  return originalIds.size !== changedIds.size || [...originalIds].some((id) => !changedIds.has(id));
}

function omitIdPatch(patch: Record<string, unknown>): Record<string, unknown> {
  if (!('id' in patch)) {
    return patch;
  }
  const { id: _id, ...rest } = patch;
  return rest;
}

/**
 * Deep-merges a `differences()` patch into an original record. Nested objects are merged recursively;
 * scalars and arrays from the patch replace the original value.
 */
function mergeRecordPatch(
  original: Record<string, unknown>,
  patch: Record<string, unknown>,
): Record<string, unknown> {
  const merged: Record<string, unknown> = { ...original };

  for (const [key, patchValue] of Object.entries(patch)) {
    const origValue = original[key];
    if (isRecord(origValue) && isRecord(patchValue) && Object.keys(patchValue).length > 0) {
      merged[key] = mergeRecordPatch(origValue, patchValue);
      continue;
    }
    merged[key] = patchValue;
  }

  return merged;
}

/**
 * Merges partial `differences()` patches back into the original object for form resets.
 * Empty array elements (`{}`) are treated as unchanged. Array elements with `id` are matched by id
 * (so reordered rows merge against the correct original) and merged recursively.
 * When `differences()` emits a full array replacement (length change, id-membership change, or
 * mixed id coverage), that array is assigned as-is rather than index-merged.
 */
export function mergeChangesIntoOriginal(
  original: Record<string, unknown>,
  changes: Record<string, unknown>,
): Record<string, unknown> {
  const merged: Record<string, unknown> = { ...original };

  for (const [key, change] of Object.entries(changes)) {
    const orig = original[key];

    if (Array.isArray(orig) && Array.isArray(change)) {
      if (orig.length !== change.length) {
        merged[key] = change;
        continue;
      }

      // Same-length scalar list replacement from differences() — assign as-is.
      // Index-merging would treat each string/number as "not an object patch" and keep orig.
      if (arrayElementsArePrimitives(orig) && arrayElementsArePrimitives(change)) {
        merged[key] = change;
        continue;
      }

      const origHasIds = arrayElementsHaveIds(orig);
      const changeHasIds = arrayElementsHaveIds(change);

      // differences() emits a full array when id membership changes or when only one side is
      // fully identifiable (e.g. a newly appended row without an id). Do not index-merge those.
      if (origHasIds && changeHasIds && arrayIdMembershipChanged(orig, change)) {
        merged[key] = change;
        continue;
      }
      if (origHasIds && !changeHasIds) {
        merged[key] = change;
        continue;
      }

      const matchById = origHasIds;
      const origById = matchById
        ? new Map(
            orig.map((origEl) => {
              const id = getIdentifiableId(origEl);
              return [id as string, origEl];
            }),
          )
        : undefined;

      merged[key] = change.map((patch, index) => {
        const origEl = orig[index];
        if (patch === null || typeof patch !== 'object' || Array.isArray(patch)) {
          return origEl;
        }
        const patchObj = patch as Record<string, unknown>;
        const patchId = getIdentifiableId(patch);
        const baseEl =
          matchById && patchId !== undefined && origById?.has(patchId)
            ? origById.get(patchId)
            : origEl;

        const patchFields = patchId !== undefined ? omitIdPatch(patchObj) : patchObj;
        if (Object.keys(patchFields).length === 0) {
          return baseEl ?? origEl;
        }
        if (!isRecord(baseEl)) {
          return patch;
        }

        return mergeRecordPatch(baseEl, patchFields);
      });
      continue;
    }

    if (
      typeof orig === 'object' &&
      orig !== null &&
      !Array.isArray(orig) &&
      typeof change === 'object' &&
      change !== null &&
      !Array.isArray(change)
    ) {
      merged[key] = mergeChangesIntoOriginal(
        orig as Record<string, unknown>,
        change as Record<string, unknown>,
      );
      continue;
    }

    merged[key] = change;
  }

  return merged;
}

/**
 * Fixes invalid UTF-16 surrogate pairs in a string by replacing them with the replacement character.
 * This prevents JSON serialization errors when strings contain lone surrogates.
 */
function fixInvalidSurrogates(str: string): string {
  let result = '';
  for (let i = 0; i < str.length; i++) {
    const code = str.charCodeAt(i);
    if (code >= 0xd800 && code <= 0xdbff) {
      // Leading surrogate
      if (i + 1 >= str.length || str.charCodeAt(i + 1) < 0xdc00 || str.charCodeAt(i + 1) > 0xdfff) {
        // Lone leading surrogate - replace with replacement character
        result += '\ufffd';
      } else {
        // Valid surrogate pair
        const nextChar = str[i + 1];
        if (nextChar !== undefined) {
          result += str[i] + nextChar;
        } else {
          result += '\ufffd';
        }
        i++; // Skip trailing surrogate
      }
    } else if (code >= 0xdc00 && code <= 0xdfff) {
      // Lone trailing surrogate - replace with replacement character
      result += '\ufffd';
    } else {
      result += str[i];
    }
  }
  return result;
}

/**
 * Nesting limit for {@link sanitizeObject}. Deeper unique objects (not cycles) become `"[max depth]"`.
 * WeakSet cycle detection does not stop stack overflow when each level is a new object or getter result.
 */
export const MAX_SANITIZE_OBJECT_DEPTH = 32;

/**
 * Recursively sanitizes an object for JSON / ZenStack `JsonValue` persistence.
 * Redacts functions, fixes invalid UTF-16 surrogates, converts BigInt and Date, replaces circular
 * refs with `"[circular]"`, and caps nesting at {@link MAX_SANITIZE_OBJECT_DEPTH}. Also strips
 * `undefined` object keys, coerces `undefined` array slots to `null`, and replaces non-finite
 * numbers with `null` so Zod JsonValue validation (used by ZenStack writes) does not reject the
 * payload.
 * @param obj The value to sanitize.
 * @param visited Identity set used to detect circular references. Callers should omit this.
 * @returns A JSON-safe copy compatible with ZenStack Json field writes.
 */
export function sanitizeObject(obj: unknown, visited = new WeakSet<object>()): unknown {
  return sanitizeValue(obj, visited, 0);
}

function sanitizeValue(obj: unknown, visited: WeakSet<object>, depth: number): unknown {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (typeof obj === 'string') {
    return fixInvalidSurrogates(obj);
  } else if (typeof obj === 'number') {
    // Zod z.number() rejects NaN/Infinity; ZenStack JsonValue uses that schema on writes.
    return Number.isFinite(obj) ? obj : null;
  } else if (typeof obj === 'boolean') {
    return obj;
  } else if (typeof obj === 'bigint') {
    // JSON has no BigInt; persist as string so JSON serialization never throws.
    return obj.toString();
  } else if (obj instanceof Date) {
    return obj.toISOString();
  } else if (typeof obj === 'function') {
    return '[function]';
  }

  if (typeof obj !== 'object') {
    return obj;
  }

  if (depth >= MAX_SANITIZE_OBJECT_DEPTH) {
    return '[max depth]';
  }

  if (Array.isArray(obj)) {
    if (visited.has(obj)) {
      return '[circular]';
    }
    visited.add(obj);
    // JsonValue arrays allow null but not undefined — coerce holes / explicit undefined to null.
    return obj.map((item) => {
      const sanitizedItem = sanitizeValue(item, visited, depth + 1);
      return sanitizedItem === undefined ? null : sanitizedItem;
    });
  }

  if (visited.has(obj)) {
    return '[circular]';
  }
  visited.add(obj);

  const sanitized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    const fixedKey = fixInvalidSurrogates(key);
    const sanitizedValue = sanitizeValue(value, visited, depth + 1);
    // Omit undefined keys so z.record(JsonValue|null) accepts the object.
    if (sanitizedValue !== undefined) {
      sanitized[fixedKey] = sanitizedValue;
    }
  }
  return sanitized;
}

/**
 * Returns true when `obj` has property `field` (same semantics as the `in` operator) and the value
 * is a string with at least one character.
 * @param field Property name to check
 * @param obj Object to inspect
 */
export function isStringFieldInObject(field: string, obj: object): boolean {
  if (!(field in obj)) {
    return false;
  }
  const value = (obj as Record<string, unknown>)[field];
  return typeof value === 'string' && value.length > 0;
}

function isValueEmpty(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.keys(value).length === 0
  ) {
    return true;
  }
  return false;
}

/**
 * Recursively traverses an object and omits fields whose value is `null`, `undefined`, `""`, `[]`, or `{}`.
 * Nested objects and array elements are processed recursively; empty arrays and empty objects are omitted as field values.
 * @param obj The object to traverse (plain object, array, or primitive)
 * @param format Optional key-order map. When present, keys in the returned object are ordered: format keys first (in format order), then any remaining keys.
 * @returns A new object/array with empty fields omitted; primitives are returned unchanged
 */
export function omitEmptyFields<T>(obj: T, format?: ObjectToMarkdownFormat): T {
  if (obj === null || obj === undefined) {
    return obj;
  }
  if (typeof obj !== 'object') {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map((item) => omitEmptyFields(item)) as T;
  }
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (isValueEmpty(value)) continue;
    const fieldFormat = format?.[key];
    const nestedFormat =
      fieldFormat != null && isNestedFormat(fieldFormat) ? fieldFormat : undefined;
    const processed = omitEmptyFields(value, nestedFormat);
    if (isValueEmpty(processed)) continue;
    result[key] = processed;
  }
  if (format != null) {
    const resultKeys = Object.keys(result);
    const orderedKeys = [
      ...Object.keys(format).filter((k) => resultKeys.includes(k)),
      ...resultKeys.filter((k) => !(k in format)),
    ];
    const sorted: Record<string, unknown> = {};
    for (const key of orderedKeys) {
      sorted[key] = result[key];
    }
    return sorted as T;
  }
  return result as T;
}

/**
 * Per-field formatter for objectToMarkdown: (key, value) → markdown string for that field.
 */
export type ObjectToMarkdownFormatter = (key: string, value: any) => string; // eslint-disable-line @typescript-eslint/no-explicit-any

/**
 * Recursive format map for objectToMarkdown. At each level, a key may map to:
 * - `null` – use default markdown (only affects key order at this level)
 * - a formatter function – render this field with the returned string
 * - a nested ObjectToMarkdownFormat – use for nested objects (key order + formatters)
 */
export interface ObjectToMarkdownFormat {
  [key: string]: null | ObjectToMarkdownFormatter | ObjectToMarkdownFormat;
}

function isNestedFormat(
  value: null | ObjectToMarkdownFormatter | ObjectToMarkdownFormat,
): value is ObjectToMarkdownFormat {
  return value !== null && typeof value === 'object';
}

/**
 * Converts an object to markdown text. Keys become headings (camelCase → Start Case); string values
 * become text, string arrays become list items, nested objects recurse with deeper heading levels;
 * other values are rendered as text.
 * @param obj The object to convert
 * @param headingLevel Starting heading level (1 = H1, 2 = H2, etc.). Default 2.
 * @param format Optional key→formatter map. Defines key order; formatter(key, value) replaces default markdown for that field.
 * @returns Markdown string
 */
export function objectToMarkdown(
  obj: object,
  headingLevel = 2,
  format?: ObjectToMarkdownFormat,
): string {
  const lines: string[] = [];
  const prefix = '#'.repeat(Math.min(Math.max(headingLevel, 1), 6));

  const objKeys = Object.keys(obj);
  const orderedKeys =
    format != null
      ? [
          ...Object.keys(format).filter((k) => objKeys.includes(k)),
          ...objKeys.filter((k) => !(k in format)),
        ]
      : objKeys;

  for (const key of orderedKeys) {
    if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;
    const value = (obj as Record<string, unknown>)[key];
    const fieldFormat = format?.[key];

    if (typeof fieldFormat === 'function') {
      lines.push(fieldFormat(key, value));
      lines.push('');
      continue;
    }

    const heading = titleCase(lowerCase(key));
    lines.push(`${prefix} ${heading}`);
    lines.push('');

    if (value === null || value === undefined) {
      lines.push(String(value));
    } else if (typeof value === 'string') {
      lines.push(value);
    } else if (Array.isArray(value)) {
      const allStrings = value.length > 0 && value.every((item) => typeof item === 'string');
      if (allStrings) {
        for (const item of value as string[]) {
          lines.push(`- ${item}`);
        }
      } else {
        for (const item of value) {
          lines.push(
            `- ${typeof item === 'object' && item !== null ? JSON.stringify(item) : String(item)}`,
          );
        }
      }
    } else if (
      typeof value === 'object' &&
      value !== null &&
      !Array.isArray(value) &&
      !(value instanceof Date)
    ) {
      const nestedFormat =
        fieldFormat != null && isNestedFormat(fieldFormat) ? fieldFormat : undefined;
      lines.push(
        objectToMarkdown(value as Record<string, unknown>, headingLevel + 1, nestedFormat),
      );
    } else {
      lines.push(value instanceof Date ? value.toISOString() : String(value));
    }

    lines.push('');
  }

  return lines
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trimEnd();
}
